import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { BookOpen, Search, Loader2 } from 'lucide-react';
import { getGuides } from '@/services/api';
import type { Guide } from '@/types';

interface AddGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (guideIds: number[]) => Promise<void>;
  helpCenterId: number;
  categoryId?: number | null;
  existingItemIds?: number[]; // Guide IDs already in the help center
  isSubmitting?: boolean;
  error?: string | null;
}

export function AddGuideModal({
  isOpen,
  onClose,
  onAdd,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  helpCenterId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  categoryId,
  existingItemIds = [],
  isSubmitting = false,
  error,
}: AddGuideModalProps) {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGuideIds, setSelectedGuideIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (isOpen) {
      fetchGuides();
      setSearchQuery('');
      setSelectedGuideIds(new Set());
    }
  }, [isOpen]);

  const fetchGuides = async () => {
    try {
      setLoading(true);
      const response = await getGuides();
      if (response.ok && response.guides) {
        setGuides(response.guides);
      }
    } catch (err) {
      console.error('Error fetching guides:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleGuide = (guideId: number) => {
    setSelectedGuideIds((prev) => {
      const next = new Set(prev);
      if (next.has(guideId)) {
        next.delete(guideId);
      } else {
        next.add(guideId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const allGuideIds = filteredGuides.map((guide) => parseInt(guide.id, 10));
    setSelectedGuideIds(new Set(allGuideIds));
  };

  const handleSelectNone = () => {
    setSelectedGuideIds(new Set());
  };

  const handleAddGuides = async () => {
    if (selectedGuideIds.size === 0) return;

    const guideIdsArray = Array.from(selectedGuideIds);
    await onAdd(guideIdsArray);
  };

  // Filter guides that are not already in the help center
  const availableGuides = guides.filter(
    (guide) => !existingItemIds.includes(parseInt(guide.id, 10))
  );

  // Filter by search query
  const filteredGuides = availableGuides.filter((guide) =>
    guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (guide.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Guide to Help Center</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search guides..."
              className="pl-9"
            />
          </div>

        

          {/* Guides List */}
          <div className="flex-1 overflow-y-auto border rounded-md">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredGuides.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery
                  ? 'No guides found matching your search'
                  : availableGuides.length === 0
                  ? 'All guides are already in this help center'
                  : 'No guides available'}
              </div>
            ) : (
              <div className="divide-y">
                {filteredGuides.map((guide) => {
                  const guideId = parseInt(guide.id, 10);
                  const isSelected = selectedGuideIds.has(guideId);
                  
                  return (
                    <div
                      key={guide.id}
                      onClick={() => handleToggleGuide(guideId)}
                      className={`w-full p-4 hover:bg-accent transition-colors cursor-pointer ${
                        isSelected ? 'bg-accent' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isSelected}
                          className="mt-0.5 flex-shrink-0"
                        />
                        <BookOpen className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{guide.title}</div>
                          {guide.description && (
                            <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {guide.description}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {guide.isPublished && (
                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded">
                                Published
                              </span>
                            )}
                            {guide.publishSettings?.accessPasswordEnabled && (
                              <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded">
                                Password Protected
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-destructive">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
              {/* Select All / Select None */}
          {filteredGuides.length > 0 && (
            <div className="flex items-center gap-2 text-sm mr-auto">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-primary hover:underline"
              >
                Select All
              </button>
              <span className="text-muted-foreground">•</span>
              <button
                type="button"
                onClick={handleSelectNone}
                className="text-primary hover:underline"
              >
                Select None
              </button>
            </div>
          )}
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddGuides}
              disabled={isSubmitting || selectedGuideIds.size === 0}
            >
              {isSubmitting 
                ? 'Adding...' 
                : selectedGuideIds.size === 0
                ? 'Add Guides'
                : `Add ${selectedGuideIds.size} Guide${selectedGuideIds.size > 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
